import { useState, useCallback, useEffect } from "react";
import { SubscriptionService, TenantSubscription, SubscriptionResponse } from "../feature-module/crm/subscriptions/subscription.service";

interface PaginationData {
  currentPage: number;
  totalCount: number;
  pageSize: number;
  hasNext: boolean;
  hasPrevious: boolean;
}

interface UseSubscriptionsReturn {
  subscriptions: TenantSubscription[];
  loading: boolean;
  backgroundLoading: boolean;
  error: string | null;
  pagination: PaginationData;
  addSubscription: (subscription: TenantSubscription) => void;
  updateSubscription: (subscription: TenantSubscription) => void;
  removeSubscription: (id: number) => void;
  fetchSubscriptions: (page?: number, pageSize?: number, params?: Record<string, any>) => Promise<void>;
  isInitialized: boolean;
  clearBackgroundLoading: () => void;
  clearSubscriptionsData: () => void;
}

export const useSubscriptions = (): UseSubscriptionsReturn => {
  const [subscriptions, setSubscriptions] = useState<TenantSubscription[]>([]);
  const [loading, setLoading] = useState(false);
  const [backgroundLoading, setBackgroundLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const [pagination, setPagination] = useState<PaginationData>({
    currentPage: 1,
    totalCount: 0,
    pageSize: 10,
    hasNext: false,
    hasPrevious: false,
  });

  const fetchSubscriptions = useCallback(async (page: number = 1, pageSize: number = 10, params?: Record<string, any>) => {
    try {
      if (!isInitialized) {
        setLoading(true);
      } else {
        setBackgroundLoading(true);
      }
      setError(null);

      const response: SubscriptionResponse = await SubscriptionService.getSubscriptions(page, pageSize, params);
      
      setSubscriptions(response.results || []);
      setPagination({
        currentPage: page,
        totalCount: response.count || 0,
        pageSize: pageSize,
        hasNext: !!response.next,
        hasPrevious: !!response.previous,
      });

      if (!isInitialized) {
        setIsInitialized(true);
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "Failed to fetch subscriptions";
      setError(errorMessage);
      console.error("Error fetching subscriptions:", err);
    } finally {
      setLoading(false);
      setBackgroundLoading(false);
    }
  }, [isInitialized]);

  const addSubscription = useCallback((subscription: TenantSubscription) => {
    setSubscriptions(prev => [subscription, ...prev]);
    setPagination(prev => ({
      ...prev,
      totalCount: prev.totalCount + 1,
    }));
  }, []);

  const updateSubscription = useCallback((updatedSubscription: TenantSubscription) => {
    setSubscriptions(prev =>
      prev.map(subscription =>
        subscription.tenant_subscription_id === updatedSubscription.tenant_subscription_id
          ? updatedSubscription
          : subscription
      )
    );
  }, []);

  const removeSubscription = useCallback((id: number) => {
    setSubscriptions(prev => prev.filter(subscription => subscription.tenant_subscription_id !== id));
    setPagination(prev => ({
      ...prev,
      totalCount: Math.max(0, prev.totalCount - 1),
    }));
  }, []);

  const clearBackgroundLoading = useCallback(() => {
    setBackgroundLoading(false);
  }, []);

  const clearSubscriptionsData = useCallback(() => {
    setSubscriptions([]);
    setError(null);
    setIsInitialized(false);
    setPagination({
      currentPage: 1,
      totalCount: 0,
      pageSize: 10,
      hasNext: false,
      hasPrevious: false,
    });
  }, []);

  return {
    subscriptions,
    loading,
    backgroundLoading,
    error,
    pagination,
    addSubscription,
    updateSubscription,
    removeSubscription,
    fetchSubscriptions,
    isInitialized,
    clearBackgroundLoading,
    clearSubscriptionsData,
  };
};
